import RiskBadge from '../components/RiskBadge';
import { formatDate } from '../utils/formatDate';
import usePageTitle from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ClipboardList,
  Building2,
  User,
  Calendar,
  CheckCircle,
  Clock,
  Play,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { inspectionsService, facilitiesService, usersService } from '../services';
import { Inspection, Task, Facility, User as UserType } from '../types';
import ActionDetailModal from '../components/Forms/ActionDetailModal';
import './InspectionDetail.css';

const InspectionDetail: React.FC = () => {
  usePageTitle('Inspection detail');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [facility, setFacility] = useState<Facility | null>(null);
  const [creatorName, setCreatorName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);

  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      void usersService.getUsers().then(setUsers).catch(() => {});
      const [inspectionData, tasksData] = await Promise.all([
        inspectionsService.getInspectionById(id),
        inspectionsService.getInspectionTasks(id),
      ]);
      setInspection(inspectionData);
      setTasks(tasksData);

      const facilityData = await facilitiesService.getFacilityById(
        inspectionData.facility_id
      );
      setFacility(facilityData);

      try {
        const creator = await usersService.getUserById(inspectionData.created_by_id);
        setCreatorName(creator.name);
      } catch {
        setCreatorName(inspectionData.created_by_id);
      }
    } catch (err) {
      console.error('Error loading inspection:', err);
      setError('Failed to load inspection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadTasksOnly = async () => {
    if (!id) return;
    try {
      const tasksData = await inspectionsService.getInspectionTasks(id);
      setTasks(tasksData);
    } catch (err) {
      console.error('Error refreshing tasks:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'not_started':
        return <Clock size={16} />;
      case 'started':
        return <Play size={16} />;
      case 'complete':
        return <CheckCircle size={16} />;
      case 'cancelled':
        return <XCircle size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="inspection-detail">
        <div className="loading-spinner">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  if (error || !inspection) {
    return (
      <div className="inspection-detail">
        <button
          type="button"
          className="btn btn-ghost back-link"
          onClick={() => navigate(inspection ? `/facilities/${inspection.facility_id}?tab=inspections` : '/facilities')}
        >
          <ArrowLeft size={20} />
          Back to Inspections
        </button>
        <div className="error-message">{error || 'Inspection not found.'}</div>
      </div>
    );
  }

  return (
    <div className="inspection-detail">
      <button
        type="button"
        className="btn btn-ghost back-link"
        onClick={() => navigate(inspection ? `/facilities/${inspection.facility_id}?tab=inspections` : '/facilities')}
      >
        <ArrowLeft size={20} />
        Back to Inspections
      </button>

      <div className="inspection-detail-header card">
        <div className="inspection-detail-icon">
          <ClipboardList size={32} />
        </div>
        <div className="inspection-detail-meta">
          <h1>
            {facility?.name ?? 'Facility'} – {inspection.type ?? 'Inspection'}
          </h1>
          {inspection.summary && <p style={{ whiteSpace: 'pre-wrap' }}>{inspection.summary}</p>}
          <div
            className={`inspection-detail-status status-${(inspection.status ?? '').replace('_', '-')}`}
          >
            {getStatusIcon(inspection.status)}
            <span>{(inspection.status ?? '').replace('_', ' ')}</span>
          </div>
          <div className="inspection-detail-info">
            <span className="info-item">
              <Building2 size={16} />
              {facility?.name ?? inspection.facility_id}
            </span>
            <span className="info-item">
              <User size={16} />
              {creatorName || inspection.created_by_id}
            </span>
            <span className="info-item">
              <Calendar size={16} />
              {formatDate(inspection.created_at)}
            </span>
          </div>
        </div>
      </div>

      <div className="inspection-tasks-section card">
        <h2>Tasks ({tasks.length})</h2>
        {tasks.length === 0 ? (
          <p className="empty-tasks">No tasks for this inspection.</p>
        ) : (
          <div className="inspection-tasks-table-wrap">
            <table className="inspection-tasks-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Responsible</th>
                  <th>Status</th>
                  <th>Due</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td>{task.description} <RiskBadge likelihood={task.risk_likelihood} consequence={task.risk_consequence} /></td>
                    <td>
                      {users.find((u) => u.id === task.responsible_user_id)?.name ??
                        task.responsible_user_name ??
                        task.responsible_user_id}
                    </td>
                    <td>
                      <span
                        className={`task-status-badge status-${(task.status ?? '').replace('_', '-')}`}
                      >
                        {getStatusIcon(task.status)}
                        {(task.status ?? '').replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      {task.due_by
                        ? formatDate(task.due_by)
                        : '–'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => setSelectedTaskId(task.id)}
                        title="View / Reassign / Complete"
                      >
                        <ExternalLink size={16} />
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActionDetailModal
        isOpen={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        taskId={selectedTaskId}
        onUpdated={loadTasksOnly}
      />
    </div>
  );
};

export default InspectionDetail;
